import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, MapPin, Download, Edit3, Upload, X,
  Loader2, CheckCircle2, XCircle, Circle, FileText, Camera,
  AlertCircle, RefreshCw, Calendar, FileDown, Radar, Search, HandCoins, Ban, MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { exportTunggakanPdf, exportTunggakanDocx } from "@/utils/tunggakanReport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: "Belum Ditagih", emoji: "🔴", color: "bg-rose-100 text-rose-700 border-rose-200" },
  ditagih: { label: "Sudah Ditagih", emoji: "🟠", color: "bg-amber-100 text-amber-700 border-amber-200" },
  janji_bayar: { label: "Janji Bayar", emoji: "🔵", color: "bg-sky-100 text-sky-700 border-sky-200" },
  lunas: { label: "Lunas", emoji: "🟢", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  tidak_ketemu: { label: "Tidak Ketemu", emoji: "⚫", color: "bg-slate-200 text-slate-600 border-slate-300" },
};

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;

// Biaya admin per bulan tunggakan (samakan dengan default PDAMTebo agar rupiah sinkron
// dengan yang ditagih saat pembayaran).
const ADMIN_FEE_PER_BULAN = 2500;

// Total efektif per pelanggan: tagihan murni + (opsional) biaya admin per bulan.
const effectiveTotal = (item, adminEnabled) =>
  Number(item?.total_tagihan || 0) +
  (adminEnabled ? Number(item?.jml_bulan || 0) * ADMIN_FEE_PER_BULAN : 0);

// Normalize an Indonesian phone number to intl format (62...) for wa.me.
const normalizePhone = (raw) => {
  let p = String(raw || "").replace(/[^\d+]/g, "");
  if (!p) return "";
  if (p.startsWith("+")) p = p.slice(1);
  if (p.startsWith("0")) p = "62" + p.slice(1);
  else if (p.startsWith("8")) p = "62" + p;
  return p;
};

// Editable WhatsApp template with placeholders. Persisted per-device in localStorage.
const WA_TEMPLATE_KEY = "tunggakan_wa_template";
const DEFAULT_WA_TEMPLATE =
  "Yth. Bpk/Ibu {nama}, tagihan air PDAM No. Samb {id} tercatat menunggak {jml_bulan} bulan " +
  "sebesar {total}. Mohon segera melakukan pembayaran. Terima kasih.";

const getSavedTemplate = () => {
  try { return localStorage.getItem(WA_TEMPLATE_KEY) || DEFAULT_WA_TEMPLATE; }
  catch { return DEFAULT_WA_TEMPLATE; }
};

const renderTemplate = (tpl, item, totalOverride) =>
  String(tpl || DEFAULT_WA_TEMPLATE)
    .replaceAll("{nama}", item.nama || "Pelanggan")
    .replaceAll("{id}", item.customer_id || "")
    .replaceAll("{jml_bulan}", String(item.jml_bulan || 0))
    .replaceAll("{total}", fmtRp(totalOverride != null ? totalOverride : item.total_tagihan));

const compressImage = (file, maxWidth = 800, quality = 0.75) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function Tunggakan() {
  const { id } = useParams();
  return id ? <TunggakanDetail listId={id} /> : <TunggakanIndex />;
}

// ============ INDEX PAGE ============
function TunggakanIndex() {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScan, setShowScan] = useState(false);
  const authToken = localStorage.getItem("user_token") || localStorage.getItem("admin_token");
  const userRole = (localStorage.getItem("user_role") || "").toLowerCase() || (localStorage.getItem("admin_token") ? "admin" : "");
  const isAllowed = useMemo(() => ["petugas", "admin"].includes(userRole) && !!authToken, [userRole, authToken]);

  const fetchLists = useCallback(async () => {
    if (!isAllowed) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tunggakan-lists`, { headers: { Authorization: `Bearer ${authToken}` } });
      setLists(res.data?.lists || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar.");
    } finally { setLoading(false); }
  }, [isAllowed, authToken]);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  // Refresh lists when a background scan auto-saves a new list.
  useEffect(() => {
    const onDone = () => fetchLists();
    window.addEventListener("tunggakan-scan-done", onDone);
    return () => window.removeEventListener("tunggakan-scan-done", onDone);
  }, [fetchLists]);

  const handleDelete = async (lid, name) => {
    if (!window.confirm(`Hapus daftar "${name}"?`)) return;
    try {
      await axios.delete(`${API}/tunggakan-lists/${lid}`, { headers: { Authorization: `Bearer ${authToken}` } });
      toast.success("Daftar dihapus.");
      fetchLists();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus."); }
  };

  if (!isAllowed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-gradient-to-br from-rose-50 to-pink-50" data-testid="tunggakan-forbidden">
        <Ban className="w-12 h-12 text-rose-400 mb-3" />
        <h2 className="font-bold text-lg text-slate-700 mb-1">Akses Ditolak</h2>
        <p className="text-sm text-slate-600 mb-4">Fitur Tagihan Menunggak khusus untuk petugas.</p>
        <Button onClick={() => navigate("/peta-global")} variant="outline">Kembali ke Peta Global</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-50" data-testid="tunggakan-page">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/peta-global")} className="gap-1" data-testid="tunggakan-back-btn">
            <ArrowLeft className="w-4 h-4" /> Peta
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-base text-rose-700 truncate flex items-center gap-1.5">
              <HandCoins className="w-5 h-5" /> Tagihan Menunggak
            </h1>
            <p className="text-[11px] text-slate-500 truncate">Pindai pelanggan menunggak & tandai penagihan</p>
          </div>
          <Button size="sm" className="gap-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setShowScan(true)} data-testid="open-scan-btn">
            <Radar className="w-4 h-4" /> Pindai
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Memuat...</div>
        ) : lists.length === 0 ? (
          <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
            <HandCoins className="w-10 h-10 text-rose-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-700 mb-1">Belum ada daftar tunggakan</h3>
            <p className="text-sm text-slate-500 mb-4">Pindai pelanggan yang menunggak untuk membuat daftar penagihan.</p>
            <Button className="gap-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setShowScan(true)} data-testid="empty-scan-btn">
              <Radar className="w-4 h-4" /> Pindai Tunggakan
            </Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="tunggakan-lists">
            {lists.map((l) => (
              <div key={l.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3" data-testid={`tunggakan-list-${l.id}`}>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/tunggakan/${l.id}`)}>
                  <div className="font-semibold text-slate-800 truncate">{l.name}</div>
                  <div className="text-[11px] text-slate-500 flex flex-wrap gap-2 mt-0.5">
                    <span>{l.counts.total} pelanggan</span>
                    <span className="text-rose-600">🔴 {l.counts.pending}</span>
                    <span className="text-amber-600">🟠 {l.counts.ditagih}</span>
                    <span className="text-sky-600">🔵 {l.counts.janji_bayar}</span>
                    <span className="text-emerald-600">🟢 {l.counts.lunas}</span>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => navigate(`/tunggakan/${l.id}`)} data-testid={`open-list-${l.id}-btn`}>
                  <Edit3 className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => handleDelete(l.id, l.name)} data-testid={`delete-list-${l.id}-btn`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showScan && (
        <ScanModal authToken={authToken} onClose={() => setShowScan(false)} />
      )}
    </div>
  );
}

// ============ SCAN MODAL (background) ============
// The scan runs on the backend and auto-saves to history when done. The user can
// close this modal / do other things — a global floating indicator tracks progress.
function ScanModal({ authToken, onClose }) {
  const [prefix, setPrefix] = useState("0215");
  const [status, setStatus] = useState("Aktif");
  const [minBulan, setMinBulan] = useState(3);
  const [maxBulan, setMaxBulan] = useState(6);
  const [name, setName] = useState(() => {
    const now = new Date();
    return `Tunggakan ${now.toLocaleDateString("id-ID")} ${now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}`;
  });
  const [starting, setStarting] = useState(false);

  const startScan = async () => {
    if (Number(maxBulan) < Number(minBulan)) return toast.error("Bulan maksimum harus ≥ minimum.");
    if (!name.trim()) return toast.error("Nama daftar wajib diisi.");
    setStarting(true);
    try {
      await axios.post(`${API}/tunggakan/scan/start`,
        {
          prefix: prefix.trim(), status: status.trim(),
          min_bulan: Number(minBulan), max_bulan: Number(maxBulan),
          auto_save: true, list_name: name.trim(),
        },
        { headers: { Authorization: `Bearer ${authToken}` } });
      localStorage.setItem("tunggakan_scan_active", "1");
      window.dispatchEvent(new Event("tunggakan-scan-started"));
      toast.success("Pemindaian berjalan di latar belakang. Anda bisa lanjut aktivitas lain — hasil tersimpan otomatis saat selesai.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memulai pemindaian.");
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-3" data-testid="scan-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white p-4 flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2"><Radar className="w-5 h-5" /> Pindai Tagihan Menunggak</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-scan-modal"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Nama Daftar</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="scan-name-input" />
            <p className="text-[10px] text-slate-500 mt-1">Daftar tersimpan otomatis dengan nama ini saat pindai selesai.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Awalan ID Pelanggan (prefix)</label>
            <Input value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="Mis: 0215" className="font-mono" data-testid="scan-prefix-input" />
            <p className="text-[10px] text-slate-500 mt-1">Kosongkan untuk semua ID. Pengecekan urut dari ID terkecil ke terbesar.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Status Pelanggan</label>
            <Input value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Aktif" data-testid="scan-status-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Tunggakan min (bulan)</label>
              <Input type="number" min={1} value={minBulan} onChange={(e) => setMinBulan(e.target.value)} data-testid="scan-min-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Tunggakan maks (bulan)</label>
              <Input type="number" min={1} value={maxBulan} onChange={(e) => setMaxBulan(e.target.value)} data-testid="scan-max-input" />
            </div>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-[11px] text-rose-700 flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Sistem mengecek tunggakan tiap pelanggan langsung ke server PDAM secara hati-hati (dengan cek ulang otomatis bila gagal) agar data akurat. Anda boleh menutup jendela ini — proses tetap jalan di latar belakang.</span>
          </div>
          <Button className="w-full gap-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={startScan} disabled={starting} data-testid="start-scan-btn">
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />} Mulai Pindai (Latar Belakang)
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ DETAIL PAGE ============
function TunggakanDetail({ listId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editItem, setEditItem] = useState(null);
  const [showBulkWa, setShowBulkWa] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [adminEnabled, setAdminEnabled] = useState(true);
  const authToken = localStorage.getItem("user_token") || localStorage.getItem("admin_token");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tunggakan-lists/${listId}`, { headers: { Authorization: `Bearer ${authToken}` } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar.");
      if (e?.response?.status === 404) navigate("/tunggakan");
    } finally { setLoading(false); }
  }, [listId, authToken, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Re-fetch when the background refresh job finishes (updates arrears / auto-lunas).
  useEffect(() => {
    const onDone = () => fetchData();
    window.addEventListener("tunggakan-refresh-done", onDone);
    return () => window.removeEventListener("tunggakan-refresh-done", onDone);
  }, [fetchData]);

  const startRefresh = async () => {
    try {
      await axios.post(`${API}/tunggakan-lists/${listId}/refresh/start`, {}, { headers: { Authorization: `Bearer ${authToken}` } });
      localStorage.setItem("tunggakan_refresh_active", "1");
      window.dispatchEvent(new Event("tunggakan-refresh-started"));
      toast.success("Refresh tagihan berjalan di latar belakang. Yang sudah lunas otomatis ditandai — Anda bisa lanjut aktivitas lain.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memulai refresh tagihan.");
    }
  };

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    if (filter === "all") return data.items;
    return data.items.filter((it) => (it.status || "pending") === filter);
  }, [data, filter]);

  const handleRemoveItem = async (cid) => {
    if (!window.confirm("Hapus pelanggan dari daftar?")) return;
    try {
      await axios.delete(`${API}/tunggakan-lists/${listId}/items/${cid}`, { headers: { Authorization: `Bearer ${authToken}` } });
      toast.success("Item dihapus.");
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus."); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Memuat daftar...</div>;
  if (!data) return null;

  const c = data.counts;
  const totalTagihan = (data.items || []).reduce((s, it) => s + effectiveTotal(it, adminEnabled), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-50" data-testid="tunggakan-detail-page">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/tunggakan")} className="gap-1" data-testid="detail-back-btn">
            <ArrowLeft className="w-4 h-4" /> Daftar
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-base text-rose-700 truncate flex items-center gap-1.5" data-testid="detail-list-name">
              <HandCoins className="w-4 h-4" /> {data.name}
            </h1>
            <p className="text-[11px] text-slate-500">{c.total} pelanggan · {fmtRp(totalTagihan)} · diperbarui {new Date(data.updated_at).toLocaleString("id-ID")}</p>
          </div>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setShowRename(true)} data-testid="rename-list-btn"><Edit3 className="w-3.5 h-3.5" /></Button>
        </div>
        <div className="max-w-4xl mx-auto px-4 pb-3 flex flex-wrap gap-1.5">
          {[
            { key: "all", label: `Semua ${c.total}`, color: "bg-slate-100 text-slate-700 border-slate-300" },
            { key: "pending", label: `${STATUS_META.pending.emoji} Belum ${c.pending}`, color: STATUS_META.pending.color },
            { key: "ditagih", label: `${STATUS_META.ditagih.emoji} Ditagih ${c.ditagih}`, color: STATUS_META.ditagih.color },
            { key: "janji_bayar", label: `${STATUS_META.janji_bayar.emoji} Janji ${c.janji_bayar}`, color: STATUS_META.janji_bayar.color },
            { key: "lunas", label: `${STATUS_META.lunas.emoji} Lunas ${c.lunas}`, color: STATUS_META.lunas.color },
            { key: "tidak_ketemu", label: `${STATUS_META.tidak_ketemu.emoji} Tidak Ketemu ${c.tidak_ketemu}`, color: STATUS_META.tidak_ketemu.color },
          ].map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`text-xs px-3 py-1 rounded-full border transition ${filter === f.key ? "bg-rose-600 text-white border-rose-700 shadow-sm" : `${f.color} hover:opacity-80`}`}
              data-testid={`filter-${f.key}-btn`}>{f.label}</button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-3 flex flex-wrap gap-2">
        <Button size="sm" className="gap-1 bg-pink-600 hover:bg-pink-700 text-white" onClick={() => navigate(`/peta-global?tunggakan=${listId}`)} data-testid="open-on-map-btn">
          <MapPin className="w-4 h-4" /> Buka di Peta Global
        </Button>
        <Button size="sm" className="gap-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => setShowBulkWa(true)} data-testid="open-bulk-wa-btn">
          <MessageCircle className="w-4 h-4" /> Kirim WA Beruntun
        </Button>
        <Button size="sm" variant="outline" className="gap-1 border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setShowExport(true)} data-testid="open-export-report-btn">
          <FileDown className="w-4 h-4" /> Export Laporan
        </Button>
        <Button size="sm" className="gap-1 bg-cyan-600 hover:bg-cyan-700 text-white" onClick={startRefresh} data-testid="refresh-tagihan-btn">
          <RefreshCw className="w-4 h-4" /> Refresh Tagihan
        </Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={fetchData} data-testid="refresh-list-btn"><RefreshCw className="w-4 h-4" /></Button>
        <button
          type="button"
          onClick={() => setAdminEnabled((v) => !v)}
          className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition ${adminEnabled ? "bg-cyan-600 text-white border-cyan-700 shadow-sm" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
          data-testid="tunggakan-admin-toggle-btn"
          title="Tampilkan total termasuk biaya admin (samakan dengan nominal saat pembayaran)"
        >
          <span className={`inline-flex h-4 w-7 items-center rounded-full transition ${adminEnabled ? "bg-white/40" : "bg-slate-300"}`}>
            <span className={`h-3 w-3 rounded-full bg-white shadow transition-transform ${adminEnabled ? "translate-x-3.5" : "translate-x-0.5"}`} />
          </span>
          Biaya Admin {adminEnabled ? `ON · ${fmtRp(ADMIN_FEE_PER_BULAN)}/bln` : "OFF"}
        </button>
      </div>
      {adminEnabled && (
        <div className="max-w-4xl mx-auto px-4 pt-1.5 text-[11px] text-slate-500" data-testid="admin-fee-legend">
          * Total sudah termasuk biaya admin {fmtRp(ADMIN_FEE_PER_BULAN)}/bulan (sesuai nominal saat pembayaran).
        </div>
      )}

      <div className="max-w-4xl mx-auto p-4 space-y-2">
        {filteredItems.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">Tidak ada item dengan filter ini.</div>
        ) : filteredItems.map((it) => {
          const s = it.status || "pending";
          const meta = STATUS_META[s] || STATUS_META.pending;
          return (
            <div key={it.customer_id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-start gap-3" data-testid={`tunggakan-item-${it.customer_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="font-mono font-bold text-sm text-slate-800">{it.customer_id}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.color}`}>{meta.emoji} {meta.label}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-bold">{it.jml_bulan} bln</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200">{fmtRp(effectiveTotal(it, adminEnabled))}{adminEnabled && Number(it.jml_bulan || 0) > 0 ? " *" : ""}</span>
                  {s === "janji_bayar" && it.promised_date && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {it.promised_date}</span>
                  )}
                </div>
                {it.nama && <div className="text-sm text-slate-700 truncate">{it.nama}</div>}
                {it.alamat && <div className="text-xs text-slate-500 truncate">{it.alamat}</div>}
                {it.note && <div className="text-[11px] text-amber-700 italic mt-1">📝 {it.note}</div>}
                {it.photo && (
                  <button type="button" onClick={() => setLightbox({ src: it.photo, title: `${it.customer_id} · ${it.nama || ""}` })} className="block mt-1 group" data-testid={`open-photo-${it.customer_id}-btn`}>
                    <img src={it.photo} alt="bukti" className="w-12 h-12 object-cover rounded border group-hover:ring-2 group-hover:ring-rose-400 transition cursor-zoom-in" />
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="outline" className="gap-1 text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => navigate(`/pdam-tebo?id=${it.customer_id}&from=tunggakan&list=${listId}`)} data-testid={`cek-tagihan-${it.customer_id}-btn`}><Search className="w-3 h-3" /> Cek Tagihan</Button>
                <Button size="sm" variant="outline" className="gap-1 text-xs h-8" onClick={() => setEditItem(it)} data-testid={`edit-item-${it.customer_id}-btn`}><Edit3 className="w-3 h-3" /> Ubah</Button>
                <Button size="sm" variant="ghost" className="gap-1 text-xs h-8 text-rose-600 hover:bg-rose-50" onClick={() => handleRemoveItem(it.customer_id)} data-testid={`remove-item-${it.customer_id}-btn`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          );
        })}
      </div>

      {editItem && (
        <EditItemModal item={editItem} listId={listId} authToken={authToken} onClose={() => setEditItem(null)}
          onUpdated={(updatedItem, newCounts) => {
            setData((prev) => ({ ...prev, items: prev.items.map((i) => i.customer_id === updatedItem.customer_id ? { ...i, ...updatedItem } : i), counts: newCounts || prev.counts }));
            setEditItem(null);
          }} />
      )}
      {showRename && (
        <RenameModal currentName={data.name} listId={listId} authToken={authToken} onClose={() => setShowRename(false)}
          onRenamed={(newName) => { setData((prev) => ({ ...prev, name: newName })); setShowRename(false); }} />
      )}
      {showExport && <ExportReportModal list={data} onClose={() => setShowExport(false)} />}
      {showBulkWa && <BulkWaModal items={data.items || []} adminEnabled={adminEnabled} onClose={() => setShowBulkWa(false)} />}
      {lightbox && (
        <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <img src={lightbox.src} alt={lightbox.title} className="max-w-full max-h-[85vh] rounded-lg" />
        </div>
      )}
    </div>
  );
}

// ============ EDIT ITEM MODAL ============
function EditItemModal({ item, listId, authToken, onClose, onUpdated }) {
  const [status, setStatus] = useState(item.status || "pending");
  const [note, setNote] = useState(item.note || "");
  const [photo, setPhoto] = useState(item.photo || "");
  const [promisedDate, setPromisedDate] = useState(item.promised_date || "");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar.");
    try { setPhoto(await compressImage(file, 800, 0.75)); } catch { toast.error("Gagal memproses foto."); }
  };

  const handleSave = async () => {
    if (status === "janji_bayar" && !promisedDate) return toast.error("Pilih tanggal janji bayar dulu.");
    setSaving(true);
    const payload = { status, note, photo, promised_date: status === "janji_bayar" ? promisedDate : "" };
    const tid = toast.loading("Menyimpan...");
    try {
      const res = await axios.patch(`${API}/tunggakan-lists/${listId}/items/${item.customer_id}`, payload, { headers: { Authorization: `Bearer ${authToken}` } });
      toast.success("Tersimpan.", { id: tid });
      onUpdated(res.data.item, res.data.counts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan.", { id: tid });
    } finally { setSaving(false); }
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const OPTS = [
    { key: "pending", label: "Belum", icon: Circle, color: "border-rose-300 text-rose-700 hover:bg-rose-50", active: "bg-rose-600 border-rose-700" },
    { key: "ditagih", label: "Sudah Ditagih", icon: CheckCircle2, color: "border-amber-300 text-amber-700 hover:bg-amber-50", active: "bg-amber-600 border-amber-700" },
    { key: "janji_bayar", label: "Janji Bayar", icon: Calendar, color: "border-sky-300 text-sky-700 hover:bg-sky-50", active: "bg-sky-600 border-sky-700" },
    { key: "lunas", label: "Lunas", icon: HandCoins, color: "border-emerald-300 text-emerald-700 hover:bg-emerald-50", active: "bg-emerald-600 border-emerald-700" },
    { key: "tidak_ketemu", label: "Tidak Ketemu", icon: XCircle, color: "border-slate-300 text-slate-600 hover:bg-slate-50", active: "bg-slate-600 border-slate-700" },
  ];

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="edit-item-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white p-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-bold text-base flex items-center gap-2"><Edit3 className="w-4 h-4" /> Status Penagihan</h2>
            <p className="text-xs opacity-90 font-mono truncate">{item.customer_id} · {item.nama || "-"}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-edit-item-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-2">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {OPTS.map((opt) => (
                <button key={opt.key} type="button" onClick={() => setStatus(opt.key)}
                  className={`px-2 py-3 rounded-lg border-2 flex flex-col items-center gap-1 text-xs font-semibold transition ${status === opt.key ? `${opt.active} text-white shadow-sm` : `bg-white ${opt.color}`}`}
                  data-testid={`status-${opt.key}-btn`}>
                  <opt.icon className="w-5 h-5" /> {opt.label}
                </button>
              ))}
            </div>
          </div>
          {status === "janji_bayar" && (
            <div data-testid="promised-date-section">
              <label className="text-xs font-semibold text-slate-700 block mb-1">Tanggal Janji Bayar <span className="text-rose-600">*</span></label>
              <Input type="date" value={promisedDate} min={todayIso} onChange={(e) => setPromisedDate(e.target.value)} className="text-sm" data-testid="promised-date-input" />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Catatan</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder="Catatan penagihan..." data-testid="note-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Foto Bukti</label>
            {photo ? (
              <div className="relative inline-block">
                <img src={photo} alt="bukti" className="w-24 h-24 object-cover rounded-lg border" />
                <button onClick={() => setPhoto("")} className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full p-1" data-testid="remove-photo-btn"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <Button type="button" variant="outline" className="gap-1" onClick={() => fileRef.current?.click()} data-testid="add-photo-btn"><Camera className="w-4 h-4" /> Ambil / Pilih Foto</Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoSelect} className="hidden" data-testid="photo-input" />
          </div>
        </div>
        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose} data-testid="cancel-edit-btn">Batal</Button>
          <Button className="flex-1 gap-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-edit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Simpan
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ RENAME MODAL ============
function RenameModal({ currentName, listId, authToken, onClose, onRenamed }) {
  const [name, setName] = useState(currentName || "");
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!name.trim()) return toast.error("Nama wajib diisi.");
    setSaving(true);
    try {
      await axios.put(`${API}/tunggakan-lists/${listId}`, { name: name.trim() }, { headers: { Authorization: `Bearer ${authToken}` } });
      toast.success("Nama diperbarui.");
      onRenamed(name.trim());
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah nama."); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="rename-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-4 space-y-3">
        <h2 className="font-bold text-base text-slate-800">Ubah Nama Daftar</h2>
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="rename-input" />
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Batal</Button>
          <Button className="flex-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-rename-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</Button>
        </div>
      </div>
    </div>
  );
}

// ============ BULK WHATSAPP (SEQUENTIAL) MODAL ============
function BulkWaModal({ items, adminEnabled, onClose }) {
  const [template, setTemplate] = useState(() => getSavedTemplate());
  const recipients = useMemo(
    () => (items || []).filter((it) => (it.status || "pending") === "pending" && normalizePhone(it.nomor_telp).length >= 9),
    [items]
  );
  const skipped = useMemo(
    () => (items || []).filter((it) => (it.status || "pending") === "pending" && normalizePhone(it.nomor_telp).length < 9),
    [items]
  );
  const [phase, setPhase] = useState("intro"); // intro | sending
  const [idx, setIdx] = useState(0);

  const saveTemplate = () => {
    try { localStorage.setItem(WA_TEMPLATE_KEY, template); toast.success("Template disimpan."); }
    catch { toast.error("Gagal menyimpan template."); }
  };

  const current = recipients[idx];

  const openCurrent = () => {
    if (!current) return;
    const phone = normalizePhone(current.nomor_telp);
    const msg = renderTemplate(template, current, effectiveTotal(current, adminEnabled));
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    setIdx((i) => i + 1);
  };
  const skipCurrent = () => setIdx((i) => i + 1);

  const done = phase === "sending" && idx >= recipients.length;

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="bulk-wa-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2"><MessageCircle className="w-5 h-5" /> Kirim WA Beruntun</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-bulk-wa-modal"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          {phase === "intro" && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800">
                Akan mengirim ke <b>{recipients.length}</b> pelanggan berstatus <b>Belum Ditagih</b> yang punya nomor HP.
                {skipped.length > 0 && <span className="block mt-1 text-amber-700">{skipped.length} pelanggan dilewati karena tidak ada nomor HP.</span>}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Template Pesan</label>
                <Textarea value={template} onChange={(e) => setTemplate(e.target.value)} rows={5} data-testid="bulk-wa-template-input" />
                <p className="text-[10px] text-slate-500 mt-1">Placeholder: <code>{"{nama}"}</code> <code>{"{id}"}</code> <code>{"{jml_bulan}"}</code> <code>{"{total}"}</code></p>
                <button onClick={saveTemplate} className="text-[11px] text-green-700 hover:underline font-semibold mt-1" data-testid="save-template-btn">💾 Simpan sebagai template default</button>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-[11px] text-slate-600">
                Cara kerja: sistem membuka chat WhatsApp pelanggan satu per satu. Di tiap chat, Anda cukup tekan tombol <b>kirim</b> di WhatsApp, lalu kembali ke sini dan tekan <b>Berikutnya</b>.
              </div>
              <Button className="w-full gap-1 bg-green-600 hover:bg-green-700 text-white" disabled={recipients.length === 0} onClick={() => { setIdx(0); setPhase("sending"); }} data-testid="start-bulk-wa-btn">
                <MessageCircle className="w-4 h-4" /> Mulai ({recipients.length})
              </Button>
            </>
          )}

          {phase === "sending" && !done && (
            <div className="space-y-3" data-testid="bulk-wa-sending">
              <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                <div className="bg-green-600 h-2.5 transition-all" style={{ width: `${Math.round((idx / recipients.length) * 100)}%` }} />
              </div>
              <div className="text-center text-xs text-slate-500">{idx} / {recipients.length} terkirim</div>
              <div className="bg-white border-2 border-green-200 rounded-xl p-3 text-center">
                <div className="font-mono font-bold text-slate-800">{current?.customer_id}</div>
                <div className="text-sm text-slate-700">{current?.nama}</div>
                <div className="text-xs text-slate-500">📞 {current?.nomor_telp}</div>
                <div className="text-[11px] text-rose-600 mt-1">{current?.jml_bulan} bln · {fmtRp(effectiveTotal(current, adminEnabled))}</div>
              </div>
              <Button className="w-full gap-1 bg-green-600 hover:bg-green-700 text-white" onClick={openCurrent} data-testid="open-current-wa-btn">
                <MessageCircle className="w-4 h-4" /> Buka Chat WhatsApp
              </Button>
              <Button variant="outline" className="w-full text-slate-600" onClick={skipCurrent} data-testid="skip-current-wa-btn">Lewati Pelanggan Ini</Button>
            </div>
          )}

          {done && (
            <div className="text-center py-6 space-y-3" data-testid="bulk-wa-done">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
              <div className="font-semibold text-slate-700">Selesai!</div>
              <div className="text-sm text-slate-500">Semua {recipients.length} chat sudah dibuka.</div>
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white" onClick={onClose} data-testid="finish-bulk-wa-btn">Tutup</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ EXPORT MODAL ============
function ExportReportModal({ list, onClose }) {
  const [photoSize, setPhotoSize] = useState("medium");
  const [busy, setBusy] = useState(false);
  const doExport = async (kind) => {
    setBusy(true);
    const tid = toast.loading("Membuat laporan...");
    try {
      if (kind === "pdf") await exportTunggakanPdf(list, photoSize);
      else await exportTunggakanDocx(list, photoSize);
      toast.success("Laporan diunduh.", { id: tid });
      onClose();
    } catch (e) { toast.error("Gagal membuat laporan.", { id: tid }); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="export-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-4 space-y-4">
        <h2 className="font-bold text-base text-slate-800 flex items-center gap-2"><FileDown className="w-5 h-5 text-rose-600" /> Export Laporan</h2>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-2">Ukuran Foto Bukti</label>
          <div className="grid grid-cols-4 gap-2">
            {[["none", "Tanpa"], ["small", "Kecil"], ["medium", "Sedang"], ["large", "Besar"]].map(([k, l]) => (
              <button key={k} onClick={() => setPhotoSize(k)} className={`text-xs py-2 rounded-lg border transition ${photoSize === k ? "bg-rose-600 text-white border-rose-700" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`} data-testid={`photo-size-${k}-btn`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button className="flex-1 gap-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => doExport("pdf")} disabled={busy} data-testid="export-pdf-btn"><FileDown className="w-4 h-4" /> PDF</Button>
          <Button className="flex-1 gap-1 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => doExport("docx")} disabled={busy} data-testid="export-docx-btn"><FileText className="w-4 h-4" /> Word</Button>
        </div>
        <Button variant="ghost" className="w-full" onClick={onClose}>Tutup</Button>
      </div>
    </div>
  );
}
